import { core } from "../../lib/api";

export interface AgentSummary {
  agent_id: string;
  agent_name?: string;
  name?: string;
}

export interface AgentOutfield {
  fieldName: string;
  fieldType?: string;
  validValues?: any[];
}

/** Call inputs (contact metadata the call is dialed with). The
 *  `/versions/current` endpoint normalises these to `{key, label}`. */
export interface AgentInfield {
  key: string;
  label?: string;
  required?: boolean;
  sample?: string | null;
}

export interface AgentCurrentVersion {
  version_id: string;
  version_name?: string;
  call_outcomes?: AgentOutfield[];
  infields?: AgentInfield[];
}

export interface WorkflowCondition {
  field: string;
  operator: "equals" | "not_equals" | "in" | "is_set" | "is_not_set";
  value?: any;
}

export interface WorkflowDelay {
  value: number;
  unit: "minutes" | "hours" | "days";
}

export type WorkflowNodeType = "call" | "email" | "whatsapp" | "sms";
export type RecipientSource = "customer_phone" | "outfield";

/** Template an email step renders and queues. `{{outfield}}` tokens are
 *  substituted server-side at enqueue time, not here. */
export interface EmailAction {
  to: string;
  cc: string[];
  subject: string;
  body: string;
}

/** Message a whatsapp / sms step sends. Field names match the backend
 *  (`message_body` / `recipient_source` / `recipient_field`) verbatim — the
 *  post-call engine reads them straight off the frozen node.
 *  Whatsapp template mode (`message_kind === "template"`): `template_id`
 *  picks a saved whatsapp-templates row, `template_mapping` maps each numeric
 *  `{{n}}` to an outfield/infield name. The engine renders it to plain text
 *  (same as campaigns) — no delivery change. */
export interface MessageAction {
  message_kind?: "free_text" | "template";
  message_body: string;
  recipient_source: RecipientSource;
  recipient_field: string;
  /** Whatsapp-only; attaches the selected Chatbot agent after this message is sent. */
  ai_agent_handoff?: boolean;
  ai_agent_id?: string;
  /** Whatsapp-only; per-step sender override. Empty = workflow default
   *  (client's workflow-purpose instance). Persisted verbatim for the engine. */
  whatsapp_instance_name?: string;
  /** Whatsapp-only; template mode. Saved template row id (or name). */
  template_id?: string;
  template_name?: string;
  /** Whatsapp-only; `{"1": "lead_name"}` — numeric variable to field name. */
  template_mapping?: Record<string, string>;
  /** Whatsapp-only; frozen copy publish wrote (body/buttons/media). Read-only. */
  template_snapshot?: any;
}

export interface WorkflowNode {
  node_id: string;
  agent_id: string;
  agent_name?: string;
  version_id?: string | null;
  version_name?: string | null;
  label: string;
  position: { x: number; y: number };
  /** Omitted on nodes authored before action steps existed — the server
   *  defaults it to "call", which is what those nodes always were. */
  node_type?: WorkflowNodeType;
  email?: EmailAction | null;
  whatsapp?: MessageAction | null;
  sms?: MessageAction | null;
}

export interface WorkflowEdge {
  edge_id: string;
  from_node_id: string;
  to_node_id: string;
  priority: number;
  conditions: WorkflowCondition[];
  delay?: WorkflowDelay | null;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  status: "draft" | "published" | "archived";
  workflow_version: number;
  start_node_id: string;
  max_steps: number;
  max_node_visits: number;
  /** The Evolution instance every whatsapp step in this workflow sends from. */
  whatsapp_instance_name?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  created_at: string;
  updated_at: string;
}

export interface CreateWorkflowPayload {
  name: string;
  description?: string;
  /** Optional — the server derives it from the seeded start node. */
  start_node_id?: string;
  /** Agent picked in the New Workflow dialog; becomes the first (start) node. */
  start_agent_id?: string;
  start_agent_name?: string;
  start_version_id?: string | null;
  start_version_name?: string;
  max_steps?: number;
  max_node_visits?: number;
  whatsapp_instance_name?: string;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
}

export interface UpdateWorkflowPayload {
  name?: string;
  description?: string;
  status?: "draft" | "published" | "archived";
  start_node_id?: string;
  max_steps?: number;
  max_node_visits?: number;
  whatsapp_instance_name?: string;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
}

export const listWorkflows = (status?: string) => {
  const url = status ? `/api/v1/call-workflows?status=${status}` : `/api/v1/call-workflows`;
  return core<Workflow[]>(url);
};

export const createWorkflow = (payload: CreateWorkflowPayload) => {
  return core<Workflow>(`/api/v1/call-workflows`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const getWorkflow = (id: string) => {
  return core<Workflow>(`/api/v1/call-workflows/${id}`);
};

export const updateWorkflow = (id: string, payload: UpdateWorkflowPayload) => {
  return core<Workflow>(`/api/v1/call-workflows/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
};

export const deleteWorkflow = (id: string, permanent: boolean = false) => {
  const url = permanent ? `/api/v1/call-workflows/${id}?permanent=true` : `/api/v1/call-workflows/${id}`;
  return core<{ deleted: boolean; workflow_id?: string; permanent?: boolean }>(url, {
    method: "DELETE",
  });
};

export const publishWorkflow = (workflowId: string) => {
  return core<{ version_id: string; status: string }>(`/api/v1/call-workflows/${workflowId}/publish`, {
    method: "POST"
  });
};

export interface WorkflowVersion {
  workflow_id: string;
  workflow_version: number;
  start_node_id: string;
  max_steps?: number;
  max_node_visits?: number;
  whatsapp_instance_name?: string;
  published_at?: string;
  published_by?: string;
  node_count: number;
  edge_count: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** Every frozen snapshot in call_workflow_versions, newest first. */
export const listWorkflowVersions = (workflowId: string) => {
  return core<WorkflowVersion[]>(`/api/v1/call-workflows/${workflowId}/versions`);
};

export const listAgentsSummary = () => {
  return core<AgentSummary[]>("/api/v2/agents?view=summary");
};

export const listMultiAgentPrompts = () => {
  return core<AgentSummary[]>("/api/v2/multi-agent-prompts?view=summary");
};

export const getAgentCurrentVersion = (agentId: string) => {
  return core<AgentCurrentVersion>(`/api/v1/agents/${encodeURIComponent(agentId)}/versions/current`);
};

export interface WorkflowRunOutfield {
  field: string;
  label?: string;
  type?: string | null;
  value?: unknown;
  is_set?: boolean;
  redacted?: boolean;
}

export interface WorkflowRunConditionEvaluation {
  field: string;
  operator?: string;
  expected?: unknown;
  actual?: unknown;
  matched?: boolean;
}

export interface WorkflowRunEdgeEvaluation {
  edge_id: string;
  to_node_id?: string;
  matched?: boolean;
  conditions?: WorkflowRunConditionEvaluation[];
}

export interface WorkflowRunHistoryEntry {
  node_id: string;
  agent_id?: string;
  call_id?: string;
  enabled_outfields?: WorkflowRunOutfield[];
  edge_evaluations?: WorkflowRunEdgeEvaluation[];
  decision?: string;
  reason_code?: string;
  reason?: string;
  matched_edge_id?: string;
  next_node_id?: string;
  callback_id?: string;
  scheduled_for_utc?: string;
  at?: string;
}

export interface WorkflowRunItem {
  id?: string;
  run_id: string;
  workflow_id: string;
  workflow_version: number;
  client_id?: string;
  contact_id?: string;
  contact_name?: string;
  customer_phone?: string;
  origin_schedule_id?: string;
  run_key?: string;
  current_node_id: string;
  step: number;
  node_visits?: Record<string, number>;
  status: "active" | "completed" | "ended_no_match" | "exhausted" | "failed" | string;
  last_error?: string | null;
  last_outcome?: string | null;
  next_action?: string | null;
  last_decision?: string | null;
  last_node_id?: string | null;
  history?: WorkflowRunHistoryEntry[];
  created_at: string;
  updated_at: string;
}

export interface ListWorkflowRunsParams {
  status?: string | string[];
  current_node_id?: string | string[];
  search?: string;
  origin_schedule_id?: string;
  page?: number;
  page_size?: number;
}

export interface ListWorkflowRunsResponse {
  items: WorkflowRunItem[];
  page: number;
  page_size: number;
  total: number;
}

export const listWorkflowRuns = (id: string, paramsOrStatus?: string | ListWorkflowRunsParams) => {
  const query = new URLSearchParams();
  if (typeof paramsOrStatus === "string") {
    if (paramsOrStatus && paramsOrStatus !== "all") query.set("status", paramsOrStatus);
  } else if (paramsOrStatus) {
    if (paramsOrStatus.status) {
       const statusStr = Array.isArray(paramsOrStatus.status) ? paramsOrStatus.status.join(",") : paramsOrStatus.status;
       if (statusStr && statusStr !== "all") query.set("status", statusStr);
    }
    if (paramsOrStatus.current_node_id) {
       const nodeStr = Array.isArray(paramsOrStatus.current_node_id) ? paramsOrStatus.current_node_id.join(",") : paramsOrStatus.current_node_id;
       if (nodeStr && nodeStr !== "all") query.set("current_node_id", nodeStr);
    }
    if (paramsOrStatus.search) query.set("search", paramsOrStatus.search);
    if (paramsOrStatus.origin_schedule_id) query.set("origin_schedule_id", paramsOrStatus.origin_schedule_id);
    if (paramsOrStatus.page) query.set("page", String(paramsOrStatus.page));
    if (paramsOrStatus.page_size) query.set("page_size", String(paramsOrStatus.page_size));
  }
  const q = query.toString() ? `?${query.toString()}` : "";
  return core<ListWorkflowRunsResponse>(`/api/v1/call-workflows/${id}/runs${q}`);
};

export interface WorkflowRunStatsResponse {
  workflow_id: string;
  current_node_id?: string;
  total: number;
  stats: Record<string, number>;
}

export interface WorkflowNodeStatsResponse {
  workflow_id: string;
  node_stats: Record<string, Record<string, number>>;
}

export const getWorkflowStats = (id: string, currentNodeId?: string, originScheduleId?: string) => {
  const query = new URLSearchParams();
  if (currentNodeId && currentNodeId !== "all") query.set("current_node_id", currentNodeId);
  if (originScheduleId) query.set("origin_schedule_id", originScheduleId);
  const q = query.toString() ? `?${query.toString()}` : "";
  return core<WorkflowRunStatsResponse>(`/api/v1/call-workflows/${id}/runs/stats${q}`);
};

export const getWorkflowNodeStats = (id: string, originScheduleId?: string) => {
  const query = originScheduleId ? `?origin_schedule_id=${encodeURIComponent(originScheduleId)}` : "";
  return core<WorkflowNodeStatsResponse>(`/api/v1/call-workflows/${id}/runs/node-stats${query}`);
};

export const getWorkflowRun = (runId: string) => {
  return core<WorkflowRunItem>(`/api/v1/call-workflow-runs/${runId}`);
};

export const resolveWorkflowRun = (runId: string, payload: { action: string; target_node_id?: string; backfill?: Record<string, any> }) => {
  return core<WorkflowRunItem>(`/api/v1/call-workflow-runs/${runId}/resolve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const listContactBatches = async () => {
  const data = await core<any>(`/api/v1/contact-batches`);
  return Array.isArray(data) ? data : [];
};

export interface ContactBatchContact {
  contact_id: string;
  name: string;
  phone_number: string;
  metadata: Record<string, any>;
  status: string;
}

export interface ContactBatchDetail {
  batch: {
    batch_id: string;
    name: string;
    status: string;
    total_contacts: number;
    valid_contacts: number;
    rejected_contacts: number;
    tags?: string[];
    // "crm" for a batch created from CRM's "Create batch" action; undefined
    // for one uploaded directly in Workflows.
    source?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  contacts: ContactBatchContact[];
  page: number;
  page_size: number;
  total: number;
}

export const getContactBatch = (batchId: string, page = 1, pageSize = 200) => {
  return core<ContactBatchDetail>(
    `/api/v1/contact-batches/${batchId}?page=${page}&page_size=${pageSize}`,
  );
};

/** Full-list replace: send every edited row; the server re-validates and recomputes counts. */
export const replaceContactBatchContacts = (
  batchId: string,
  contacts: { name?: string; phone_number: string; contact_id?: string; metadata?: Record<string, any> }[],
) => {
  return core<any>(`/api/v1/contact-batches/${batchId}/contacts`, {
    method: "PUT",
    body: JSON.stringify({ contacts }),
  });
};

export const createContactBatch = (payload: { name: string; tags?: string[]; contacts: any[]; source?: string }) => {
  return core<any>(`/api/v1/contact-batches`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const updateContactBatch = (batchId: string, payload: { name?: string; tags?: string[]; archived?: boolean }) => {
  return core<any>(`/api/v1/contact-batches/${batchId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
};

/** One infield some agent in the workflow expects, with who expects it. */
export interface WorkflowInfield {
  fieldName: string;
  description: string;
  fieldType: string;
  agents: string[];
}

export const getWorkflowInfields = (workflowId: string) => {
  return core<WorkflowInfield[]>(`/api/v1/call-workflows/${workflowId}/infields`);
};

export interface WorkflowOutfield {
  fieldName: string;
  description: string;
  fieldType: string;
  agents: string[];
}

export const getWorkflowOutfields = (workflowId: string) => {
  return core<WorkflowOutfield[]>(`/api/v1/call-workflows/${workflowId}/outfields`);
};

export const launchContactBatch = (batchId: string, payload: { schedule: any, idempotency_key: string, infield_mapping?: Record<string, string>, outcome_status_outfield?: string }) => {
  return core<any>(`/api/v1/contact-batches/${batchId}/schedules`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};
